import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { HmDurationPipe } from '@app/shared/pipes/hm-duration.pipe';
import { ProffeoDatePipe } from '@app/shared/pipes/proffeo-date.pipe';
import { AuthService } from '@app/shared/services/auth.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { TimeReport } from '@app/shared/types/models/reports/time-report';
import { GroupingModeEnum, TableColumnsEnum } from '@app/shared/types/models/reports/time-report-panel.model';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import {
  groupTimeReportRows,
  mergeGroupSelection,
  TimeReportRowGroup
} from '@app/shared/utils/time-report-row.grouping';
import { collectSelectableRowIds } from '@app/shared/utils/time-report-table.utils';
import { TranslatePipe } from '@ngx-translate/core';
import { ReportsDataTableComponent } from '../reports-data-table/reports-data-table.component';

const ALL_COLUMNS: TableColumnsEnum[] = [
  TableColumnsEnum.SELECT,
  TableColumnsEnum.DATE,
  TableColumnsEnum.USER,
  TableColumnsEnum.COMPANY,
  TableColumnsEnum.PROJECT,
  TableColumnsEnum.TASK_ID,
  TableColumnsEnum.TASK_TITLE,
  TableColumnsEnum.LOCATION,
  TableColumnsEnum.DESCRIPTION,
  TableColumnsEnum.TYPE,
  TableColumnsEnum.COMMENT,
  TableColumnsEnum.ESTIMATION,
  TableColumnsEnum.TOTAL,
  TableColumnsEnum.STATUS,
  TableColumnsEnum.ACTIONS
];

const MOBILE_COLUMNS: TableColumnsEnum[] = [
  TableColumnsEnum.DATE,
  TableColumnsEnum.USER,
  TableColumnsEnum.PROJECT,
  TableColumnsEnum.TYPE,
  TableColumnsEnum.TOTAL,
  TableColumnsEnum.ACTIONS
];

@Component({
  selector: 'proffeo-grouped-reports',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatCheckboxModule,
    TranslatePipe,
    ProffeoDatePipe,
    HmDurationPipe,
    ReportsDataTableComponent
  ],
  templateUrl: './grouped-reports.component.html',
  styleUrl: './grouped-reports.component.scss'
})
export class GroupedReportsComponent {
  private readonly authService = inject(AuthService);
  private readonly deviceService = inject(DeviceService);
  private readonly isMobile = this.deviceService.isMobile;

  private readonly groupingModeToHiddenColumns: Record<GroupingModeEnum, TableColumnsEnum[]> = {
    [GroupingModeEnum.NONE]: [],
    [GroupingModeEnum.USER]: [TableColumnsEnum.USER],
    [GroupingModeEnum.PROJECT]: [TableColumnsEnum.PROJECT],
    [GroupingModeEnum.TASK]: [TableColumnsEnum.TASK_ID],
    [GroupingModeEnum.DATE]: [TableColumnsEnum.DATE],
    [GroupingModeEnum.USER_DATE]: [TableColumnsEnum.USER, TableColumnsEnum.DATE],
    [GroupingModeEnum.COMPANY]: [TableColumnsEnum.COMPANY],
    [GroupingModeEnum.TYPE]: [TableColumnsEnum.TYPE]
  };
  private lastGroupingMode: GroupingModeEnum | undefined;
  private knownGroupKeys = new Set<string>();
  private lastIsExpandedAll: boolean | undefined;

  protected readonly GroupingModeEnum = GroupingModeEnum;
  protected readonly TableColumnsEnum = TableColumnsEnum;

  protected readonly groupedReports = computed(() => groupTimeReportRows(this.rows(), this.groupingMode()));

  protected readonly columns = computed<TableColumnsEnum[]>(() => {
    const base = this.isMobile() ? MOBILE_COLUMNS : ALL_COLUMNS;
    const hidden = this.groupingModeToHiddenColumns[this.groupingMode()] ?? [];
    return base.filter(column => !hidden.includes(column));
  });

  protected expandedPanels = signal<Set<string>>(new Set());

  public readonly rows = input.required<TimeReportRowVM[]>();
  public readonly groupingMode = input.required<GroupingModeEnum>();
  public readonly selectedReportIds = input.required<string[]>();
  public readonly selectableReportIds = input<readonly string[] | null>(null);
  public readonly selectionDisabled = input(false);
  public readonly isExpandedAll = input<boolean>(false);
  public readonly selectedTimeReportIdsChanged = output<string[]>();
  public readonly reportEdited = output<TimeReport>();
  public readonly reportDeleted = output<TimeReport>();
  public readonly taskDetailsOpened = output<string>();

  public constructor() {
    effect(() => {
      const groups = this.groupedReports();
      const mode = this.groupingMode();
      const expandAll = this.isExpandedAll();
      const groupKeys = new Set(groups.map(group => group.key));
      const modeChanged = this.lastGroupingMode === undefined || this.lastGroupingMode !== mode;
      const commandChanged = this.lastIsExpandedAll !== undefined && this.lastIsExpandedAll !== expandAll;

      untracked(() => {
        let nextExpanded: Set<string>;
        if (modeChanged) {
          nextExpanded = this.defaultExpandedGroupKeys(groups, mode);
        } else if (commandChanged) {
          nextExpanded = expandAll ? groupKeys : new Set();
        } else {
          nextExpanded = new Set([...this.expandedPanels()].filter(key => groupKeys.has(key)));
          const defaultExpanded = this.defaultExpandedGroupKeys(groups, mode);
          for (const key of groupKeys) {
            if (!this.knownGroupKeys.has(key) && defaultExpanded.has(key)) {
              nextExpanded.add(key);
            }
          }
        }

        if (!this.setsEqual(this.expandedPanels(), nextExpanded)) {
          this.expandedPanels.set(nextExpanded);
        }
      });

      this.lastGroupingMode = mode;
      this.knownGroupKeys = groupKeys;
      this.lastIsExpandedAll = expandAll;
    });
  }

  protected toggleTimeReport(id: string): void {
    const eligible = this.groupedReports().flatMap(group => this.selectableIds(group.rows));
    if (this.selectionDisabled() || !eligible.includes(id)) {
      return;
    }
    const currentIds = this.selectedReportIds().filter(selectedId => eligible.includes(selectedId));
    const newIds = currentIds.includes(id) ? currentIds.filter(item => item !== id) : [...currentIds, id];
    this.selectedTimeReportIdsChanged.emit(newIds);
  }

  protected selectSession(rows: TimeReportRowVM[]): void {
    if (this.selectionDisabled()) return;
    const eligible = this.groupedReports().flatMap(group => this.selectableIds(group.rows));
    const sessionIds = this.selectableIds(rows).filter(id => eligible.includes(id));
    const selected = this.selectedReportIds().filter(id => eligible.includes(id));
    const allSelected = sessionIds.length > 0 && sessionIds.every(id => selected.includes(id));
    this.selectedTimeReportIdsChanged.emit(mergeGroupSelection(selected, sessionIds, allSelected));
  }

  protected selectableIds(rows: TimeReportRowVM[]): string[] {
    const allowed = this.selectableReportIds();
    return allowed === null
      ? collectSelectableRowIds(rows)
      : [...new Set(rows.flatMap(row => (row.id && allowed.includes(row.id) ? [row.id] : [])))];
  }

  protected onEditReport(report: TimeReport): void {
    this.reportEdited.emit(report);
  }

  protected onDeleteReport(report: TimeReport): void {
    this.reportDeleted.emit(report);
  }

  protected openTaskDetails(taskId: string): void {
    this.taskDetailsOpened.emit(taskId);
  }

  protected isPanelExpanded(groupId: string): boolean {
    return this.expandedPanels().has(groupId);
  }

  protected onPanelToggle(groupId: string, isExpanded: boolean): void {
    const expanded = new Set(this.expandedPanels());
    if (isExpanded) {
      expanded.add(groupId);
    } else {
      expanded.delete(groupId);
    }
    this.expandedPanels.set(expanded);
  }

  private defaultExpandedGroupKeys(groups: TimeReportRowGroup[], mode: GroupingModeEnum): Set<string> {
    if (mode !== GroupingModeEnum.USER) {
      return new Set();
    }
    const currentUserId = this.authService.currentUser()?.id;
    if (!currentUserId) {
      return new Set();
    }
    return new Set(groups.filter(group => group.rows[0]?.userId === currentUserId).map(group => group.key));
  }

  private setsEqual(left: Set<string>, right: Set<string>): boolean {
    if (left.size !== right.size) {
      return false;
    }
    return [...left].every(key => right.has(key));
  }
}
